import { LanguageCode } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { COLORS } from 'Components/foundation';
import { withTooltip } from 'Components/hoc';
import { MDrawer } from 'Components/modal';
import MRemoveLanguage from 'Components/modal/MRemoveLanguage/MRemoveLanguage';
import { UDropdownBox } from 'Components/unit';
import UButton from 'Components/unit/UButton/UButton';
import UDropdownLanguageItem from 'Components/unit/UDropdownLanguageItem/UDropdownLanguageItem';
import ULanguageListItem from 'Components/unit/ULanguageListItem/ULanguageListItem';
import { AppDispatch } from 'Libs/redux/store';
import { useTypedSelector } from 'Libs/redux/utils';
import { enqueueBasicAlert } from 'Pages/AlertManager';
import { Language, getLanguagesAvailableWithCode } from 'Services/language/languageService';
import { fetchTranslations } from 'Services/translator/translatorService';
import { updateBreakingNews } from 'Store/entities/activity/activity.thunks';
import { BreakingNews, MultiLingualLabelWithId } from 'Store/entities/activity/activity.type';

import { BreakingNewsFormValues } from '../../BreakingNewsForm.hook';
import style from './LanguagePanel.style';

export type LanguagePanelProps = {
  visible: boolean;
  isBreakingNewsLive: boolean;
  missingMandatoryFields: { [key in LanguageCode]?: string[] };
  onClose: () => void;
};

const ALERT_DURATION = 5000;

const WrappedUButton = withTooltip(UButton);

export function isMultiLingualLabelWithId(field: any): field is MultiLingualLabelWithId {
  return field && typeof field.id === 'number' && !('image' in field);
}

export const LanguagePanel = ({ visible, isBreakingNewsLive, missingMandatoryFields, onClose }: LanguagePanelProps) => {
  const { getValues, setValue } = useFormContext<BreakingNewsFormValues>();

  const dispatch = useDispatch<AppDispatch>();

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const languagesAvailable = useMemo(() => getLanguagesAvailableWithCode(), []);

  const savedBreakingNews = useTypedSelector((state) => state.entities.activity.edition.breakingNews);

  const breakingNews = useMemo(
    () => ({
      ...savedBreakingNews,
      informations: {
        ...savedBreakingNews?.informations,
        ...getValues(),
      },
    }),
    [getValues('description'), getValues('name'), getValues('text'), getValues('isLongText'), savedBreakingNews],
  );

  const selectedLanguages = useMemo(() => breakingNews?.languages ?? [], [breakingNews]);

  const sortedSelectedLanguages = useMemo(() => {
    return selectedLanguages
      .map((code) => languagesAvailable.find((language) => language.languageCode === code))
      .filter((language): language is Language => language !== undefined)
      .sort((a, b) => (a.isDefault ? -1 : b.isDefault ? 1 : 0));
  }, [selectedLanguages, languagesAvailable]);

  const generateErrorMessage = useCallback(
    (missingFields: { [key in LanguageCode]?: string[] }) => {
      const allMissingFields = new Set(Object.values(missingFields).flatMap((fields) => fields));

      return (
        <>
          <span>{t('breaking_news:edition.language.add_fail_prefix')}</span>
          <ul style={style.errorMessageList}>
            {Array.from(allMissingFields).map((field) => (
              <li key={field}>{t(`breaking_news:edition.${field}`)}</li>
            ))}
          </ul>
        </>
      );
    },
    [t],
  );

  const [languageToRemove, setLanguageToRemove] = useState<LanguageCode | null>(null);
  const [isLanguageDropdownOpen, setLanguageDropdownOpen] = useState(false);

  useEffect(() => {
    const handleClickOutsideDropdown = (event: MouseEvent) => {
      if (dropdownRef.current && event.target instanceof Node && !dropdownRef.current.contains(event.target)) {
        setLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideDropdown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideDropdown);
    };
  }, []);

  const handleRemoveLanguage = useCallback((languageCode: LanguageCode) => {
    setLanguageToRemove(languageCode);
  }, []);

  const getOptions = useCallback(
    (languageCode: LanguageCode) => [
      {
        title: t('activities:modals.language_drawer.options.remove'),
        callback: () => handleRemoveLanguage(languageCode),
        color: COLORS.ALERT.ERROR,
      },
    ],
    [handleRemoveLanguage],
  );

  const handleAddLanguage = useCallback(
    async (languageCode: LanguageCode) => {
      const { unwrap } = dispatch(
        updateBreakingNews({
          ...breakingNews,
          languages: [...selectedLanguages, languageCode],
        }),
      );

      setLanguageDropdownOpen(false);

      try {
        await unwrap();
      } catch (e) {
        enqueueBasicAlert(
          {
            id: 'breaking_news_language_add_failed',
            priority: 'medium',
            status: 'error',
            title: t('breaking_news:edition.language.add_fail'),
          },
          ALERT_DURATION,
          generateErrorMessage(missingMandatoryFields),
        );

        return;
      }

      if (!breakingNews) return;

      try {
        const selectedLanguage = selectedLanguages[0];
        const informationsKeys: (keyof BreakingNews['informations'])[] = Object.keys(
          breakingNews.informations,
        ) as (keyof BreakingNews['informations'])[];

        const itemsId = informationsKeys.reduce((acc, field) => {
          const fieldInfo = breakingNews.informations?.[field];

          if (isMultiLingualLabelWithId(fieldInfo) && fieldInfo?.id && fieldInfo[selectedLanguage]) {
            acc.push(fieldInfo.id);
          }

          return acc;
        }, [] as number[]);

        const { multilingualLabels } = await fetchTranslations({
          sourceLanguageCode: selectedLanguage,
          targetLanguageCode: languageCode,
          multilingualLabelIds: itemsId.flat(),
        });

        informationsKeys.map((field) => {
          const fieldInfo = breakingNews?.informations[field];

          if (!fieldInfo || !isMultiLingualLabelWithId(fieldInfo)) return;
          setValue(
            field as keyof BreakingNewsFormValues,
            multilingualLabels.find((label) => label.id === fieldInfo.id),
          );
        });
      } catch (error) {
        enqueueBasicAlert(
          {
            id: 'translation_failed',
            status: 'error',
            title: t('structural_component:s_translation_interface.error'),
          },
          ALERT_DURATION,
        );
      }
    },

    [dispatch, selectedLanguages, breakingNews],
  );

  const handleConfirmLanguageRemove = useCallback(async () => {
    if (!languageToRemove || !breakingNews) return;

    const filterLanguage = (info: MultiLingualLabelWithId | undefined): MultiLingualLabelWithId | undefined => {
      return info ? Object.fromEntries(Object.entries(info).filter(([key]) => key !== languageToRemove)) : info;
    };

    const updatedInformations: BreakingNews['informations'] = {
      ...breakingNews.informations,
      name: filterLanguage(breakingNews?.informations?.name),
      description: filterLanguage(breakingNews?.informations?.description),
      text: filterLanguage(breakingNews?.informations?.text),
    };

    const { unwrap } = dispatch(
      updateBreakingNews({
        ...breakingNews,
        informations: updatedInformations,
        languages: selectedLanguages.filter((code) => code !== languageToRemove),
      }),
    );

    try {
      await unwrap();
      enqueueBasicAlert(
        {
          id: 'breaking_news_language_remove_success',
          status: 'success',
          title: t('breaking_news:edition.language.remove_success'),
        },
        ALERT_DURATION,
      );
    } catch (e) {
      enqueueBasicAlert(
        {
          id: 'breaking_news_language_remove_failed',
          status: 'error',
          title: t('breaking_news:edition.language.remove_fail'),
        },
        ALERT_DURATION,
      );
    }

    setLanguageToRemove(null);
  }, [dispatch, languageToRemove, selectedLanguages, breakingNews]);

  const handleClose = useCallback(() => {
    if (languageToRemove) return;

    onClose();
  }, [onClose, languageToRemove]);

  return (
    <MDrawer
      onClose={handleClose}
      title={t('breaking_news:languages_panel.title')}
      visible={visible}
      style={style.drawer}
      data-testid="language-drawer"
    >
      <div style={style.container}>
        <div style={style.abstract}>{t('breaking_news:languages_panel.description')}</div>
        <div style={style.current}>{t('breaking_news:languages_panel.current_languages')}</div>
        {sortedSelectedLanguages.map((language) => {
          const { languageCode, isDefault, countryCode, languageName } = language;
          const options = getOptions(languageCode);

          return (
            <ULanguageListItem
              key={languageCode}
              options={options}
              defaultLabel={t('breaking_news:languages_panel.default_label')}
              isDefault={isDefault || sortedSelectedLanguages.length === 1}
              countryCode={countryCode}
              label={languageName}
              optionsTooltipText={t('breaking_news:languages_panel.cannot_change_while_live_tooltip')}
              optionsDisabled={isBreakingNewsLive}
            />
          );
        })}
        <div style={style.wrapperAddLanguages}>
          <WrappedUButton
            type="accentuated"
            text={t('breaking_news:languages_panel.add_language')}
            rightIcon="plus-light"
            onClick={() => setLanguageDropdownOpen(true)}
            rightIconSize={12}
            disabled={isBreakingNewsLive}
            tooltipPosition="right"
            tooltipContent={t('breaking_news:languages_panel.cannot_change_while_live_tooltip')}
            tooltipEnabled={isBreakingNewsLive}
            tooltipMaxWidth={400}
          />
          {isLanguageDropdownOpen && (
            <div ref={dropdownRef} style={style.containerDropdownLanguages}>
              <UDropdownBox
                items={languagesAvailable.filter((language) => !selectedLanguages.includes(language.languageCode))}
                renderItem={({ languageCode, languageName, countryCode }: Language) => (
                  <UDropdownLanguageItem
                    key={languageCode}
                    onClick={() => handleAddLanguage(languageCode)}
                    text={languageName}
                    countryCode={countryCode}
                  />
                )}
              />
            </div>
          )}
        </div>
      </div>
      <MRemoveLanguage
        visible={!!languageToRemove}
        onConfirm={handleConfirmLanguageRemove}
        onClose={() => setLanguageToRemove(null)}
        isLoading={false}
        content={t('breaking_news:languages_panel.remove_language_content')}
      />
    </MDrawer>
  );
};

export default LanguagePanel;
